import type { ConnectorErrorKind, GapDto, GapKind, GapStatus, RepairAttemptDto, SoftwareConnectionDto, SoftwareDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { useTenant } from '../../lib/useTenant';
import { Badge, Button, ErrorText } from '../../ui';

/** Nomi dei generi di mancanza (ADR 0252 § 2). */
export const GAP_KIND: Record<GapKind, string> = {
  contenuto_rimandato: 'File non scaricati',
  file_non_arrivati: 'File che non arrivano',
  lettura_vuota: 'Lettura vuota',
  numerazione_con_buchi: 'Numeri mancanti',
  errore_ripetuto: 'Errore che si ripete',
  errore_definizione: 'Lettura rifiutata dal servizio',
  elenco_svuotato: 'Elenco del sito svuotato',
  lavoro_interrotto: 'Lavoro interrotto',
  credenziali: 'Credenziali o accesso',
  servizio_non_raggiungibile: 'Servizio non raggiungibile',
};

const GAP_STATUS: Record<GapStatus, { label: string; tone: 'gray' | 'amber' | 'green' | 'red' | 'blue' }> = {
  aperta: { label: 'aperta', tone: 'amber' },
  in_prova: { label: 'il sistema la sta correggendo', tone: 'blue' },
  proposta: { label: 'correzione da approvare', tone: 'blue' },
  risolta: { label: 'risolta', tone: 'green' },
  alla_persona: { label: 'serve una persona', tone: 'red' },
  allo_sviluppo: { label: 'serve una modifica del programma', tone: 'gray' },
};

export const ERROR_KIND: Record<ConnectorErrorKind, string> = {
  credenziali: 'credenziali o permessi',
  sessione_scaduta: 'accesso scaduto',
  troppe_richieste: 'troppe richieste',
  non_raggiungibile: 'servizio non raggiungibile',
  bloccato: 'verifica, captcha o divieto',
  definizione: 'lettura superata',
  elenco_svuotato: 'elenco del sito vuoto',
  altro: 'altro',
};

function GapItem({ g }: { g: GapDto }) {
  const st = GAP_STATUS[g.status];
  return (
    <li className="rounded-lg border border-black/10 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{GAP_KIND[g.kind] ?? g.kind}</span>
        <Badge tone={st.tone}>{st.label}</Badge>
        <span className="text-xs text-ink/45">
          dal {dateTime(g.opened_at)}
          {g.resolved_at ? ` · risolta il ${dateTime(g.resolved_at)}` : ''}
        </span>
      </div>
      <p className="mt-1 text-sm text-ink/75">{g.summary}</p>
      {g.history.length > 1 && (
        <details className="mt-1 text-xs text-ink/55">
          <summary className="cursor-pointer">Storia ({g.history.length})</summary>
          <ul className="mt-1 space-y-0.5">
            {g.history.map((h, i) => (
              <li key={i}>
                {dateTime(h.at)} · {h.event}
                {h.note ? ` — ${h.note}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </li>
  );
}

const ATTEMPT_STATUS: Record<RepairAttemptDto['status'], { label: string; tone: 'gray' | 'amber' | 'green' | 'red' | 'blue' }> = {
  in_corso: { label: 'in prova', tone: 'blue' },
  fallita: { label: 'prova non riuscita', tone: 'gray' },
  proposta: { label: 'da approvare', tone: 'amber' },
  applicata: { label: 'applicata', tone: 'green' },
  annullata: { label: 'annullata', tone: 'gray' },
  rifiutata: { label: 'rifiutata', tone: 'gray' },
  scaduta: { label: 'scaduta', tone: 'gray' },
  errore: { label: 'errore', tone: 'red' },
};

/**
 * Mancanze e correzioni di un'utenza (ADR 0252 § 5): le mancanze trovate dai rilevatori con le prove, le correzioni
 * del sistema con il loro avviso e «Torna indietro», le versioni, la scelta «chiedimi prima», gli ultimi giri (lo
 * storico degli errori).
 */
export function SelfRepairInfo({ s, c }: { s: SoftwareDto; c: SoftwareConnectionDto }) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const base = `/software/${s.id}/connection`;
  const q = `?connection=${c.id}`;
  const act = useMutation({
    mutationFn: (x: { path: string; body?: unknown }) => api<SoftwareDto>(t(`${base}/${x.path}${q}`), { method: 'POST', body: x.body ?? {} }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'software'] }),
  });
  const open = c.gaps.filter((g) => g.status !== 'risolta');
  const solved = c.gaps.filter((g) => g.status === 'risolta');
  const latest = c.versions[0];
  // L'avviso dell'ultima correzione del sistema ancora in uso, con «Torna indietro».
  const applied = latest?.by_system ? c.repairs.find((r) => r.status === 'applicata') : null;
  const pending = c.repairs.filter((r) => r.status === 'proposta');
  return (
    <div className="space-y-4">
      {applied && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
          <p className="font-medium text-emerald-900">Il sistema ha corretto il collegamento: {applied.explanation}</p>
          {applied.probe && <p className="mt-1 text-emerald-900/80">Provato su {applied.probe.checked} elementi veri prima di applicarlo, il {dateTime(applied.created_at)}.</p>}
          {isManager && (
            <Button variant="ghost" className="mt-2" disabled={act.isPending} onClick={() => act.mutate({ path: 'undo' })}>
              Torna indietro
            </Button>
          )}
        </div>
      )}
      {pending.map((r) => r.source === 'mappatura' ? (
        <div key={r.id} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
          <p className="font-medium text-amber-900">{r.explanation}</p>
          <p className="mt-1 text-amber-900/80">Quando l’anteprima della nuova mappatura è pronta, confermala dalle operazioni qui sotto.</p>
        </div>
      ) : (
        <div key={r.id} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
          <p className="font-medium text-amber-900">Correzione da approvare: {r.explanation}</p>
          {r.probe && <p className="mt-1 text-amber-900/80">Provata su {r.probe.checked} elementi veri. Vale fino al {r.expires_at ? dateTime(r.expires_at) : '—'}.</p>}
          {isManager && (
            <div className="mt-2 flex gap-2">
              <Button disabled={act.isPending} onClick={() => act.mutate({ path: `repair/${r.id}/approve` })}>
                Applica
              </Button>
              <Button variant="ghost" disabled={act.isPending} onClick={() => act.mutate({ path: `repair/${r.id}/reject` })}>
                Rifiuta
              </Button>
            </div>
          )}
        </div>
      ))}
      {open.length ? (
        <ul className="space-y-2">
          {open.map((g) => (
            <GapItem key={g.id} g={g} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-ink/55">Nessuna mancanza aperta: i controlli dell’ultimo giro non hanno trovato niente.</p>
      )}
      {isManager && (
        <div className="flex flex-wrap items-center gap-3">
          {open.some((g) => g.status === 'aperta') && (
            <Button variant="ghost" disabled={act.isPending} onClick={() => act.mutate({ path: 'repair' })}>
              {act.isPending ? 'Prova in corso…' : 'Prova a correggere ora'}
            </Button>
          )}
          <label className="flex items-center gap-2 text-sm text-ink/70">
            <input
              type="checkbox"
              checked={c.repair_mode === 'chiedimi_prima'}
              disabled={act.isPending}
              onChange={(e) => act.mutate({ path: 'repair-mode', body: { mode: e.target.checked ? 'chiedimi_prima' : 'entro_limiti' } })}
            />
            Chiedimi prima di applicare una correzione
          </label>
        </div>
      )}
      {act.error && <ErrorText>{errorMessage(act.error)}</ErrorText>}
      {c.repairs.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-ink/60">Correzioni provate ({c.repairs.length})</summary>
          <ul className="mt-2 space-y-2">
            {c.repairs.map((r) => (
              <li key={r.id} className="rounded-lg border border-black/10 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={ATTEMPT_STATUS[r.status].tone}>{ATTEMPT_STATUS[r.status].label}</Badge>
                  <span className="text-xs text-ink/45">
                    {dateTime(r.created_at)} · {r.source === 'catalogo' ? 'dal catalogo' : r.source === 'motore' ? 'dalla documentazione' : r.source === 'mappatura' ? 'nuova mappatura del sito' : '—'}
                    {r.cost_usd > 0 ? ` · ${r.cost_usd.toFixed(3)} $` : ''}
                  </span>
                </div>
                {r.explanation && <p className="mt-1 text-ink/75">{r.explanation}</p>}
                {r.probe && r.probe.notes.length > 0 && (
                  <ul className="mt-1 list-disc pl-5 text-xs text-ink/55">
                    {r.probe.notes.slice(0, 5).map((n, i) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
      {c.versions.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-ink/60">Versioni della definizione ({c.versions.length})</summary>
          <ul className="mt-2 space-y-1">
            {c.versions.map((v, i) => (
              <li key={v.id} className="flex flex-wrap gap-2">
                <span className="tabular-nums text-ink/45">n. {v.number}</span>
                <span>{v.reason}</span>
                <span className="text-xs text-ink/45">
                  {v.by_system ? 'dal sistema' : 'da una persona'} · {dateTime(v.created_at)}
                  {i === 0 ? ' · in uso' : ''}
                </span>
              </li>
            ))}
          </ul>
          {isManager && c.versions.length > 1 && !applied && (
            <Button variant="ghost" className="mt-2" disabled={act.isPending} onClick={() => act.mutate({ path: 'undo' })}>
              Torna alla versione precedente
            </Button>
          )}
        </details>
      )}
      {solved.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-ink/60">Risolte negli ultimi 30 giorni ({solved.length})</summary>
          <ul className="mt-2 space-y-2">
            {solved.map((g) => (
              <GapItem key={g.id} g={g} />
            ))}
          </ul>
        </details>
      )}
      {c.runs.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-ink/60">Ultimi giri ({c.runs.length})</summary>
          <table className="bk-list mt-2 min-w-max">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Esito</th>
                <th>Errore</th>
              </tr>
            </thead>
            <tbody>
              {c.runs.map((r, i) => (
                <tr key={i}>
                  <td>{dateTime(r.at)}</td>
                  <td>{r.ok ? <Badge tone="green">riuscito</Badge> : <Badge tone="red">{r.error_kind ? ERROR_KIND[r.error_kind] : 'non riuscito'}</Badge>}</td>
                  <td className="whitespace-normal text-ink/70">
                    {r.error ? `${r.error_status ? `${r.error_status} · ` : ''}${r.error}` : <span className="text-ink/30">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}
